// @file Composable for handling drag and drop events on the whiteboard's canvas
import { ContentPickerSource } from '@@/bits/content_picker'
import { shouldEnableWhiteboardInPlaceUploadingAnimation } from '@@/bits/whiteboard_no_dependencies'
import { useWhiteboardStore } from '@@/pinia/whiteboard'
import { useWhiteboardPermissionsStore } from '@@/pinia/whiteboard_permissions'
import { useWhiteboardToolsStore } from '@@/pinia/whiteboard_tools'
import type { TLPageId } from 'tldraw'
import type { Ref } from 'vue'
import { onMounted, onUnmounted, ref } from 'vue'

export function useWhiteboardDragAndDrop(tldrawAppRef: Ref<HTMLElement | undefined>): {
  isDragging: Ref<boolean>
} {
  const isDragging = ref(false)
  const whiteboardPermissionsStore = useWhiteboardPermissionsStore()
  const whiteboardStore = useWhiteboardStore()
  const whiteboardToolsStore = useWhiteboardToolsStore()

  function startDragging(): void {
    isDragging.value = true
  }

  function stopDragging(): void {
    isDragging.value = false
  }

  function handleDragEnter(event: DragEvent): void {
    if (
      whiteboardPermissionsStore.canIAdminister ||
      (whiteboardPermissionsStore.canIWriteFrame(whiteboardStore.currentFrameId as TLPageId) &&
        whiteboardToolsStore.isToolEnabled(ContentPickerSource.FileBrowser))
    ) {
      event.preventDefault()
      startDragging()
    }
  }

  function handleDragLeave(event: DragEvent): void {
    if (tldrawAppRef.value == null) return
    const rect = tldrawAppRef.value.getBoundingClientRect()
    const isOutsideCanvas =
      event.clientX <= rect.left ||
      event.clientX >= rect.right ||
      event.clientY <= rect.top ||
      event.clientY >= rect.bottom

    if (isOutsideCanvas) {
      stopDragging()
    }
  }

  onMounted(() => {
    if (!shouldEnableWhiteboardInPlaceUploadingAnimation()) return

    tldrawAppRef.value?.addEventListener('dragenter', handleDragEnter)
    tldrawAppRef.value?.addEventListener('drop', stopDragging)
    tldrawAppRef.value?.addEventListener('dragleave', handleDragLeave)
  })

  onUnmounted(() => {
    tldrawAppRef.value?.removeEventListener('dragenter', handleDragEnter)
    tldrawAppRef.value?.removeEventListener('drop', stopDragging)
    tldrawAppRef.value?.removeEventListener('dragleave', handleDragLeave)
  })

  return {
    isDragging,
  }
}
